import React, { useState, useEffect, useCallback } from 'react';
import './VideoCarousel.css';

function formatDuration(iso) {
  const match = iso.match(/PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?/);
  if (!match) return '0:00';

  const hours = parseInt(match[1] || '0', 10);
  const minutes = parseInt(match[2] || '0', 10);
  const seconds = parseInt(match[3] || '0', 10);

  const paddedSeconds = seconds.toString().padStart(2, '0');

  if (hours > 0) {
    const paddedMinutes = minutes.toString().padStart(2, '0');
    return `${hours}:${paddedMinutes}:${paddedSeconds}`;
  }

  return `${minutes}:${paddedSeconds}`;
}

export default function VideoCarousel({ videos }) {
  const [activeIndex, setActiveIndex] = useState(0);
  const [paused, setPaused] = useState(false);

  const next = useCallback(() => {
    setActiveIndex(prev => (prev + 1) % videos.length);
  }, [videos.length]);

  // Auto-advance every 6 seconds, paused on hover
  useEffect(() => {
    if (videos.length <= 1 || paused) return;

    const interval = setInterval(next, 6000);
    return () => clearInterval(interval);
  }, [next, videos.length, paused]);

  if (!videos || videos.length === 0) return null;

  return (
    <div
      className="video-carousel"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="video-carousel-label">Latest Events</div>
      <div className="video-carousel-card">
        <div className="video-carousel-viewport">
          <div
            className="video-carousel-track"
            style={{ transform: `translateX(-${activeIndex * 100}%)` }}
          >
            {videos.map(video => (
              <a
                key={video.videoId}
                href={`https://www.youtube.com/watch?v=${video.videoId}`}
                target="_blank"
                rel="noopener noreferrer"
                className="video-carousel-slide"
              >
                <div className="video-carousel-thumb-wrapper">
                  <img
                    src={video.thumbnailUrl}
                    alt={video.title}
                    className="video-carousel-thumb"
                  />
                  <span className="video-carousel-duration">{formatDuration(video.duration)}</span>
                  <div className="video-carousel-play-overlay">
                    <svg className="video-carousel-play-icon" viewBox="0 0 48 48" fill="none">
                      <circle cx="24" cy="24" r="24" fill="rgba(0,0,0,0.55)" />
                      <path d="M19 15L35 24L19 33V15Z" fill="#F5B041" />
                    </svg>
                  </div>
                </div>
                <div className="video-carousel-info">
                  <h3 className="video-carousel-title">{video.title}</h3>
                  <p className="video-carousel-channel">{video.channelTitle}</p>
                </div>
              </a>
            ))}
          </div>
        </div>
      </div>

      {videos.length > 1 && (
        <div className="video-carousel-dots">
          {videos.map((_, i) => (
            <button
              key={i}
              className={`video-carousel-dot ${i === activeIndex ? 'active' : ''}`}
              onClick={() => setActiveIndex(i)}
              aria-label={`Go to video ${i + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}
